import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, SmartDataTable, getStatusBadge, Modal, Input } from '@/backoffice/components/ui';
import { UserPermissionsEditor } from '@/backoffice/components/admin/UserPermissionsEditor';
import {
  fetchAllUsers,
  fetchUserDetail,
  updateUserStatus,
  createUser,
  fetchZones,
  fetchAccountTypes,
  getPrimaryTypeName,
  isStaffUser,
  isSuperAdminUser,
  permissionLabel,
  type AdminUser,
} from '@/domains/admin/services/admin.service';
import { Ban, UserCheck, Shield, Plus } from 'lucide-react';

export function SuperAdminsPage() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminTypeId, setAdminTypeId] = useState<number | null>(null);
  const [defaultZoneId, setDefaultZoneId] = useState(1);
  const [newAdmin, setNewAdmin] = useState({
    firstnames: '',
    lastname: '',
    phone: '',
    email: '',
    password: '',
  });

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [users, types, zones] = await Promise.all([
        fetchAllUsers(),
        fetchAccountTypes(),
        fetchZones(),
      ]);
      setAdmins(users.filter(isStaffUser));
      setAdminTypeId(types.find((t) => t.name === 'admin')?.id ?? null);
      if (zones[0]?.id) setDefaultZoneId(zones[0].id);
    } catch {
      setError('Impossible de charger les administrateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const openAdmin = async (admin: AdminUser) => {
    try {
      setSelectedAdmin(await fetchUserDetail(admin.id));
    } catch {
      setSelectedAdmin(admin);
    }
    setShowModal(true);
  };

  const handleToggleStatus = async (admin: AdminUser) => {
    if (isSuperAdminUser(admin)) {
      setError('Un super-administrateur ne peut pas être suspendu.');
      return;
    }
    setActionLoading(true);
    try {
      const newStatus = admin.status === 'active' ? 'suspended' : 'active';
      await updateUserStatus(admin.id, newStatus);
      await loadAdmins();
      if (selectedAdmin?.id === admin.id) {
        setSelectedAdmin(await fetchUserDetail(admin.id));
      }
    } catch {
      setError('Échec de la mise à jour du statut.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminTypeId || !newAdmin.firstnames || !newAdmin.lastname || !newAdmin.phone || !newAdmin.password) return;
    setActionLoading(true);
    try {
      await createUser({
        firstnames: newAdmin.firstnames,
        lastname: newAdmin.lastname,
        phone: newAdmin.phone,
        email: newAdmin.email || null,
        password: newAdmin.password,
        status: 'active',
        zone_id: defaultZoneId,
        type_ids: [adminTypeId],
      });
      setShowCreateModal(false);
      setNewAdmin({ firstnames: '', lastname: '', phone: '', email: '', password: '' });
      await loadAdmins();
    } catch {
      setError('Échec de la création du sous-administrateur.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <DashboardLayout userType="super_admin" title="Administrateurs">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          Gérez les comptes admin / super-admin et leurs permissions
        </p>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
          Nouveau sous-admin
        </Button>
      </div>

      <Card>
        {loading ? (
          <p className="p-8 text-center text-gray-500">Chargement...</p>
        ) : (
          <SmartDataTable
            data={admins}
            columns={[
              {
                key: 'name',
                title: 'Administrateur',
                sortable: true,
                render: (item) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {item.firstnames[0]}{item.lastname[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.firstnames} {item.lastname}</p>
                      <p className="text-xs text-gray-500">{item.email ?? item.phone}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: 'types',
                title: 'Rôle',
                render: (item) => (
                  <Badge variant={getPrimaryTypeName(item) === 'super_admin' ? 'secondary' : 'primary'}>
                    {getPrimaryTypeName(item).replace('_', ' ')}
                  </Badge>
                ),
              },
              {
                key: 'status',
                title: 'Statut',
                render: (item) => getStatusBadge(item.status),
              },
              {
                key: 'permissions',
                title: 'Permissions',
                render: (item) => (
                  <div className="flex flex-wrap gap-1">
                    {(item.permissions ?? []).slice(0, 3).map((p) => (
                      <Badge key={p.id} variant="default" size="sm">{permissionLabel(p.name)}</Badge>
                    ))}
                    {(item.permissions?.length ?? 0) > 3 && (
                      <Badge variant="default" size="sm">+{(item.permissions?.length ?? 0) - 3}</Badge>
                    )}
                  </div>
                ),
              },
            ]}
            keyExtractor={(item) => String(item.id)}
            onView={openAdmin}
            onEdit={openAdmin}
            emptyMessage="Aucun administrateur"
          />
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedAdmin(null); }}
        title={selectedAdmin ? `${selectedAdmin.firstnames} ${selectedAdmin.lastname}` : 'Administrateur'}
        size="lg"
      >
        {selectedAdmin && (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-violet-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-violet-600" />
                <div>
                  <p className="font-medium text-gray-900 capitalize">
                    {getPrimaryTypeName(selectedAdmin).replace('_', ' ')}
                  </p>
                  <p className="text-sm text-gray-500">{selectedAdmin.phone}</p>
                </div>
              </div>
              {selectedAdmin.status === 'active' ? (
                !isSuperAdminUser(selectedAdmin) ? (
                  <Button
                    variant="danger"
                    size="sm"
                    leftIcon={<Ban className="w-4 h-4" />}
                    isLoading={actionLoading}
                    onClick={() => handleToggleStatus(selectedAdmin)}
                  >
                    Suspendre
                  </Button>
                ) : (
                  <Badge variant="secondary">Compte protégé</Badge>
                )
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  leftIcon={<UserCheck className="w-4 h-4" />}
                  isLoading={actionLoading}
                  onClick={() => handleToggleStatus(selectedAdmin)}
                >
                  Réactiver
                </Button>
              )}
            </div>

            <UserPermissionsEditor
              userId={selectedAdmin.id}
              readOnly={false}
              onSaved={async () => {
                await loadAdmins();
                setSelectedAdmin(await fetchUserDetail(selectedAdmin.id));
              }}
            />

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => { setShowModal(false); setSelectedAdmin(null); }}>
                Fermer
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Créer un sous-administrateur"
        size="md"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Prénoms" value={newAdmin.firstnames} onChange={(e) => setNewAdmin((a) => ({ ...a, firstnames: e.target.value }))} />
            <Input label="Nom" value={newAdmin.lastname} onChange={(e) => setNewAdmin((a) => ({ ...a, lastname: e.target.value }))} />
          </div>
          <Input label="Téléphone" value={newAdmin.phone} onChange={(e) => setNewAdmin((a) => ({ ...a, phone: e.target.value }))} />
          <Input label="Email" type="email" value={newAdmin.email} onChange={(e) => setNewAdmin((a) => ({ ...a, email: e.target.value }))} />
          <Input label="Mot de passe" type="password" value={newAdmin.password} onChange={(e) => setNewAdmin((a) => ({ ...a, password: e.target.value }))} />
          <p className="text-xs text-gray-500">
            {adminTypeId
              ? 'Les permissions pourront être configurées après la création du compte.'
              : 'Type « admin » introuvable — exécutez le seeder des types de compte.'}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button isLoading={actionLoading} disabled={!adminTypeId} onClick={handleCreateAdmin}>Créer</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
